// #73 Menu and MenuButton: a popover list under a button (DC-27) on --card-2 with --shadow and --r-input, 36 px items,
// role="menu" of role="menuitem" (a <button>, or an <a> for a link), not glass. Keyboard (DS §2.1, APG menu button):
// Enter, Space or ArrowDown on the button opens it on the first item, ArrowUp on the last; arrows rove with wrap,
// Home/End jump; Enter or Space chooses; Escape closes and returns focus to the button; Tab closes and moves on from
// the button; a pointer press outside closes. A rule-blocked item stays focusable, is aria-disabled in --text-3 and
// shows its reason under its words (the reason is its description). Absent items (can={false}) are not drawn.
// The open list portals into the nearest layer (ground rule 9: inline under SSR and before mount). Closed, an empty
// hidden element keeps the button's aria-controls target in the document. `preview` (a gallery section's static
// rendering): open, no focus moves, no outside listener.

import {
  forwardRef, useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type MutableRefObject, type RefObject,
} from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { McPortal, usePortalTarget } from "../lib/McRoot";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { useRoving } from "../lib/useRoving";
import { useSvgId } from "../lib/useSvgId";
import { vars } from "../lib/vars";
import { Button, IconButton, type Blocked, type ButtonProps, type ControlProps } from "./Button";
import { Icon, type IconName } from "./icons";

export interface MenuItem {
  /** The item's words: a verb (*Edit · View history*), a reason code, an export format. */
  label: string;
  onSelect?: (() => void) | undefined;
  /** A link item (an <a role="menuitem">). */
  href?: string | undefined;
  icon?: IconName | undefined;
  /** Rule-blocked: shown, focusable, aria-disabled, with the reason under the words. */
  blocked?: Blocked | undefined;
  /** false: the seat may not do this; the item is not drawn. */
  can?: boolean | undefined;
  hs?: Hs | undefined;
}

export type MenuCloseReason = "escape" | "select" | "outside" | "tab";

export interface MenuProps {
  items: readonly MenuItem[];
  open: boolean;
  onClose: (reason: MenuCloseReason) => void;
  /** The list's id: the button's aria-controls. */
  id: string;
  /** The opener: the list sits under it, and focus returns to it. */
  anchor: RefObject<HTMLElement | null>;
  /** Names the list (the button's id); else `label`. */
  labelledBy?: string | undefined;
  label?: string | undefined;
  /** Which item takes focus when the list opens (none: focus stays where it is). */
  autoFocus?: "first" | "last" | undefined;
  /** "end": the list's right edge meets the button's. */
  align?: "start" | "end" | undefined;
  /** "above": the list's bottom edge meets the button's top (an opener at the foot of the page). */
  placement?: "below" | "above" | undefined;
  preview?: boolean | undefined;
  className?: string | undefined;
}

function MenuEntry({ item, index, listId, roving, choose }: {
  item: MenuItem;
  index: number;
  listId: string;
  roving: ReturnType<typeof useRoving>;
  choose: (item: MenuItem, event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
}) {
  const hot = useHs(item.hs);
  const whyId = `${listId}-why-${index}`;
  const props = {
    ...roving.item(index),
    role: "menuitem",
    className: cx("mc-menu-i", item.blocked && "mc-menu-dis"),
    "aria-disabled": item.blocked ? true : undefined,
    "aria-describedby": item.blocked ? whyId : undefined,
    onClick: (event: MouseEvent<HTMLElement>) => choose(item, event),
    ...hot,
  } as const;
  const inner = (
    <>
      {item.icon ? <Icon name={item.icon} size={16} /> : null}
      <span className="mc-menu-t">
        {item.label}
        {item.blocked ? <span className="mc-menu-why" id={whyId}>{item.blocked.reason}</span> : null}
      </span>
    </>
  );
  if (item.href !== undefined && !item.blocked) {
    return (
      <a
        {...props}
        ref={props.ref as (element: HTMLAnchorElement | null) => void}
        href={item.href}
        onKeyDown={(event) => { if (event.key === " ") { event.preventDefault(); event.currentTarget.click(); } }}
      >
        {inner}
      </a>
    );
  }
  return <button {...props} ref={props.ref as (element: HTMLButtonElement | null) => void} type="button">{inner}</button>;
}

export function Menu({
  items, open, onClose, id, anchor, labelledBy, label, autoFocus, align = "start", placement = "below", preview = false, className,
}: MenuProps) {
  const shown = items.filter((item) => item.can !== false);
  const target = usePortalTarget();
  const list = useRef<HTMLDivElement | null>(null);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  const roving = useRoving({
    count: shown.length,
    orientation: "vertical",
    wrap: true,
    onEscape: () => close.current("escape"),
    returnFocusTo: anchor as RefObject<HTMLElement | null>,
  });
  const focusItem = roving.focus;

  // Placed under the opener (or above it), measured from the layer's origin; kept in place while the page scrolls or resizes.
  const place = useCallback(() => {
    if (!target || !anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    const layer = target.getBoundingClientRect();
    setAt({ x: Math.round((align === "end" ? rect.right : rect.left) - layer.left), y: Math.round((placement === "above" ? rect.top : rect.bottom) - layer.top) });
  }, [target, anchor, align, placement]);
  useIsoLayoutEffect(() => {
    if (open) place();
  }, [open, place]);
  useEffect(() => {
    if (!open || preview) return;
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, preview, place]);

  // Focus moves into the list when it opens from the button (never in a preview).
  useEffect(() => {
    if (!open || preview || !autoFocus || shown.length === 0) return;
    focusItem(autoFocus === "first" ? 0 : shown.length - 1);
    // Only on opening (focusItem and the count are left out): a later re-render keeps the roving position.
  }, [open, preview, autoFocus]);

  // A press outside the list and the opener closes it, without moving focus.
  useEffect(() => {
    if (!open || preview) return;
    const onDown = (event: PointerEvent) => {
      const node = event.target as Node | null;
      if (list.current?.contains(node) || anchor.current?.contains(node)) return;
      close.current("outside");
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open, preview, anchor]);

  const choose = (item: MenuItem, event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    if (item.blocked) {
      event.preventDefault();
      return;
    }
    item.onSelect?.();
    close.current("select");
    if (!preview) anchor.current?.focus();
  };

  if (!open) return <McPortal><div id={id} className="mc-menu-off" hidden /></McPortal>;
  const floating = target !== null && at !== null;
  return (
    <McPortal>
      <div
        ref={list}
        id={id}
        role="menu"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : label}
        aria-orientation="vertical"
        className={cx("mc-menu", align === "end" && "mc-menu-end", placement === "above" && "mc-menu-above", floating && "mc-menu-float", className)}
        style={floating ? vars({ "--mc-menu-x": at.x, "--mc-menu-y": at.y }) : undefined}
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            close.current("tab");
            anchor.current?.focus();
            return;
          }
          roving.container.onKeyDown(event);
        }}
      >
        {shown.map((item, index) => (
          <MenuEntry key={`${item.label}-${index}`} item={item} index={index} listId={id} roving={roving} choose={choose} />
        ))}
      </div>
    </McPortal>
  );
}

export interface MenuButtonProps extends ControlProps {
  /** The button's words; with iconOnly, its name (aria-label and the tooltip). */
  label: string;
  items: readonly MenuItem[];
  /** The button's icon; with iconOnly the button is an IconButton (default icon "dots"). */
  icon?: IconName | undefined;
  iconOnly?: boolean | undefined;
  tone?: ButtonProps["tone"];
  sm?: boolean | undefined;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  align?: "start" | "end" | undefined;
  /** "above": the list opens over the button. */
  placement?: "below" | "above" | undefined;
  /** A gallery section's static rendering of the open list. */
  preview?: boolean | undefined;
  /** The list's id (default generated). */
  id?: string | undefined;
  /** An element already on the page that describes the button (the row it acts on), merged into aria-describedby. */
  describedBy?: string | undefined;
  className?: string | undefined;
}

export const MenuButton = forwardRef<HTMLButtonElement, MenuButtonProps>(function MenuButton(
  { label, items, icon, iconOnly, tone = "ghost", sm, open, defaultOpen = false, onOpenChange, align, placement, preview, id, describedBy, can, blocked, mode, hs, className },
  forwarded,
) {
  const generated = useSvgId();
  const listId = id ?? `${generated}-menu`;
  const buttonId = `${generated}-btn`;
  const [own, setOwn] = useState(defaultOpen);
  const isOpen = open ?? own;
  const [focusOn, setFocusOn] = useState<"first" | "last" | undefined>(undefined);
  const button = useRef<HTMLButtonElement | null>(null);
  const setRef = (element: HTMLButtonElement | null) => {
    button.current = element;
    if (typeof forwarded === "function") forwarded(element);
    else if (forwarded) (forwarded as MutableRefObject<HTMLButtonElement | null>).current = element;
  };
  const change = (next: boolean, focus?: "first" | "last") => {
    setFocusOn(next ? focus : undefined);
    if (open === undefined) setOwn(next);
    onOpenChange?.(next);
  };
  if (can === false || mode === "view") return null;
  const trigger = {
    id: buttonId,
    "aria-haspopup": "menu" as const,
    "aria-expanded": isOpen && !blocked,
    "aria-controls": listId,
    "aria-describedby": describedBy,
    blocked,
    hs,
    sm,
    onClick: () => change(!isOpen, "first"),
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (blocked) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        change(true, event.key === "ArrowDown" ? "first" : "last");
      } else if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        change(false);
      }
    },
  };
  return (
    <span className={cx("mc-menu-anchor", className)}>
      {iconOnly
        ? <IconButton {...trigger} ref={setRef} icon={icon ?? "dots"} label={label} />
        : <Button {...trigger} ref={setRef} tone={tone} icon={icon} after="down">{label}</Button>}
      <Menu
        items={items}
        open={isOpen && !blocked}
        onClose={() => change(false)}
        id={listId}
        anchor={button}
        labelledBy={buttonId}
        autoFocus={focusOn}
        align={align}
        placement={placement}
        preview={preview}
      />
    </span>
  );
});
